import { faRedo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { FC, useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DeviceValue, IParameter } from 'src/store/models/Device';
import { zeroPad } from 'src/utils/deviceUtils';

import NumberSelect from '../../NumberSelect/NumberSelect';
import Tooltip from '../../Tooltip/Tooltip';
import classes from './Parameter.module.scss';

export interface ParameterProps {
  parameter: DeviceValue;
  parameterDetails: IParameter;
  id?: string;
}

const Parameter: FC<ParameterProps> = ({ parameter, parameterDetails, id }) => {
  const { t } = useTranslation();
  const { control, watch, formState, setValue, setError, clearErrors } =
    useFormContext<{ parameters: DeviceValue[] }>();
  const param = watch(`parameters.${parameter?.id}`);
  const isDirty = formState.dirtyFields.parameters?.[parameter?.id];

  const error = useMemo(
    () =>
      param?.value > parameterDetails?.maxValue ||
      param?.value < parameterDetails?.minValue ||
      isNaN(param?.value),
    [param?.value, parameterDetails?.maxValue, parameterDetails?.minValue]
  );

  const errorMessage = useMemo(
    () =>
      t('device_parameters:enter_value_between', {
        min: parameterDetails?.minValue,
        max: parameterDetails?.maxValue,
      }),
    [parameterDetails?.maxValue, parameterDetails?.minValue, t]
  );

  useEffect(() => {
    if (error) {
      setError(`parameters.${parameter.id}`, {
        message: errorMessage,
      });
    } else {
      clearErrors(`parameters.${parameter.id}`);
    }
    // eslint-disable-next-line
  }, [error]);

  useEffect(() => {
    return () => {
      clearErrors(`parameters.${parameter.id}`);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <div
      className={classNames(classes.Container, {
        [classes.Error]: error,
      })}
      id={id}
    >
      <p>{`${zeroPad(parameterDetails?.parameterIdRcu, 3)} / ${
        parameterDetails?.nameToShow
      }`}</p>
      <Tooltip placement="top" tooltipText={errorMessage} disabled={!error}>
        <Controller
          name={`parameters.${parameter.id}`}
          control={control}
          rules={{
            max: parameterDetails?.maxValue,
            min: parameterDetails?.minValue,
          }}
          render={({ field }) => (
            <NumberSelect
              value={field.value?.value}
              onChange={(v) =>
                setValue(
                  `parameters.${parameter?.id}`,
                  {
                    ...param,
                    value: v,
                  },
                  { shouldDirty: true }
                )
              }
              maxValue={parameterDetails?.maxValue}
              minValue={parameterDetails?.minValue}
              className={classes.NumberSelect}
            />
          )}
        />
      </Tooltip>
      <Tooltip
        placement="top"
        tooltipText={t('device_parameters:reset_changes')}
        disabled={!isDirty}
      >
        <FontAwesomeIcon
          className={classes.Reset}
          icon={faRedo}
          style={{ visibility: isDirty ? 'visible' : 'hidden' }}
          onClick={() =>
            setValue(
              `parameters.${parameter?.id}`,
              {
                ...param,
                value: parameter?.value,
              },
              { shouldDirty: true }
            )
          }
        />
      </Tooltip>
    </div>
  );
};

export default Parameter;
